import type { Dispatch, SetStateAction } from "react";
import Button from "../../../shared/components/Button";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { AuthUser } from "../../auth/shared/types/auth";
import type { PairMode } from "../../challenges/types/challengeDraft";
import { normalizeCpf } from "../../challenges/utils/challengePairs";
import AdminPlayerSearchInput from "./AdminPlayerSearchInput";

type AdminChallengeParticipantControlsProps = {
  availableUsers: AuthUser[];
  canAddParticipants?: boolean;
  currentPairMode: PairMode;
  drawCpf: string;
  isPoco: boolean;
  isSuper8: boolean;
  isTournamentChallenge: boolean;
  nameByCpf: Record<string, string>;
  onAddDrawPlayer: (rawValue?: string) => boolean;
  onAddFixedPair: () => void;
  pairCpf1: string;
  pairCpf2: string;
  pairMode: PairMode;
  resolvePlayerCpfInput: (value: string) => string;
  setDrawCpf: Dispatch<SetStateAction<string>>;
  setPairCpf1: Dispatch<SetStateAction<string>>;
  setPairCpf2: Dispatch<SetStateAction<string>>;
  setPairMode: Dispatch<SetStateAction<PairMode>>;
  super8GroupACount: number;
  maxDrawPlayersForCurrentChallenge: number | null;
  maxFixedPairsForCurrentChallenge: number | null;
  draftPairsCount: number;
  draftPlayersCount: number;
};

export default function AdminChallengeParticipantControls({
  availableUsers,
  canAddParticipants = true,
  currentPairMode,
  drawCpf,
  isPoco,
  isSuper8,
  isTournamentChallenge,
  nameByCpf,
  onAddDrawPlayer,
  onAddFixedPair,
  pairCpf1,
  pairCpf2,
  pairMode,
  resolvePlayerCpfInput,
  setDrawCpf,
  setPairCpf1,
  setPairCpf2,
  setPairMode,
  super8GroupACount,
  maxDrawPlayersForCurrentChallenge,
  maxFixedPairsForCurrentChallenge,
  draftPairsCount,
  draftPlayersCount,
}: AdminChallengeParticipantControlsProps) {
  const fixedPairLimitReached = Boolean(
    maxFixedPairsForCurrentChallenge !== null &&
      draftPairsCount >= maxFixedPairsForCurrentChallenge,
  );
  const drawPlayerLimitReached = Boolean(
    maxDrawPlayersForCurrentChallenge !== null &&
      currentPairMode === "draw" &&
      draftPlayersCount >= maxDrawPlayersForCurrentChallenge,
  );
  const canUseDrawControls = canAddParticipants && !drawPlayerLimitReached;

  return (
    <>
      <h3>
        {isSuper8
          ? siteTexts.challenges.modes.super8.title
          : siteTexts.challengeEngine.labels.buildPairs}
      </h3>

      {!isSuper8 && !isPoco && !isTournamentChallenge && (
        <div className="form-linha modo-duplas">
          <label className="modo-checkbox">
            <input
              type="checkbox"
              checked={pairMode === "draw"}
              onChange={(event) =>
                setPairMode(event.target.checked ? "draw" : "fixed")
              }
            />
            <span>{siteTexts.challengeEngine.labels.pairDrawMode}</span>
          </label>
        </div>
      )}

      {currentPairMode === "fixed" && !isSuper8 ? (
        isTournamentChallenge ? null : (
          <div className="admin-participant-add-panel">
            <div className="form-linha">
              <AdminPlayerSearchInput
                availableUsers={availableUsers}
                disabled={!canAddParticipants}
                excludeCpf={resolvePlayerCpfInput(pairCpf2)}
                id="admin-busca-jogador-dupla-1"
                onValueChange={setPairCpf1}
                placeholder={siteTexts.challengeEngine.labels.player1Placeholder}
                value={pairCpf1}
              />
              <AdminPlayerSearchInput
                availableUsers={availableUsers}
                disabled={!canAddParticipants}
                excludeCpf={resolvePlayerCpfInput(pairCpf1)}
                id="admin-busca-jogador-dupla-2"
                onValueChange={setPairCpf2}
                placeholder={siteTexts.challengeEngine.labels.player2Placeholder}
                value={pairCpf2}
              />
              <Button
                type="button"
                variant="primary"
                disabled={fixedPairLimitReached || !canAddParticipants}
                onClick={onAddFixedPair}
              >
                {siteTexts.challengeEngine.labels.add}
              </Button>
            </div>
            <div className="form-linha nomes-dupla">
              <span>
                {nameByCpf[resolvePlayerCpfInput(pairCpf1)] ||
                  siteTexts.challengeEngine.labels.player1}
              </span>
              <span>
                {nameByCpf[resolvePlayerCpfInput(pairCpf2)] ||
                  siteTexts.challengeEngine.labels.player2}
              </span>
            </div>
          </div>
        )
      ) : (
        <div className="admin-participant-add-panel">
          <div className="form-linha">
            <AdminPlayerSearchInput
              availableUsers={availableUsers}
              disabled={!canUseDrawControls}
              id="admin-busca-jogador-sorteio"
              onSelect={(_user, nextValue) => onAddDrawPlayer(nextValue)}
              onValueChange={setDrawCpf}
              placeholder={siteTexts.challengeEngine.labels.playerPlaceholder}
              value={drawCpf}
            />
            {!isSuper8 && (
              <Button
                type="button"
                variant="primary"
                disabled={!canUseDrawControls}
                onClick={() => onAddDrawPlayer()}
              >
                {siteTexts.challengeEngine.labels.add}
              </Button>
            )}
          </div>
          <div className="form-linha nomes-dupla">
            <span>
              {nameByCpf[normalizeCpf(drawCpf)] ||
                siteTexts.challengeEngine.labels.player}
            </span>
            {isSuper8 && (
              <span className="lado-preview">
                {super8GroupACount}/8 participantes
              </span>
            )}
          </div>
        </div>
      )}
    </>
  );
}
